<template>
  <div
    class="flex flex-col min-h-full"
    ref="myPresentations"
  >
    <public-announcement :is-fixed="true" />
    <invitation-notifications v-if="$store.state.currentUser.id" />
    <edu-yearly-notification v-if="$store.state.currentUser.id" />
    <div v-if="!isSearching">
      <template-panel
        v-if="$route.name !== 'SharedWithMe' && $route.name !== 'Team' && currentUser.id"
        class="flex-col hidden px-4 pt-6 pb-4 md:flex md:px-10"
      />
    </div>
    <portal to="report-panel-destination">
      <ReportPanel />
    </portal>
    <div
      class="m-4 mt-6 md:px-6"
    >
      <switch-tab-search
        v-if="!isEURegion"
        v-show="isSearching"
      />
      <!-- Breacumb for normal user, hiding when user search something -->
      <page-breadcrumb
        v-if="!isEURegion"
        v-show="!isSearching &&
          (
            $route.name === 'SharedWithMe' || $route.name === 'Team' || $route.params.folderId ||
            (!presentationsAndFoldersLoading && !empty)
          )"
        category="workspace"
        :text="breadcrumbCategory"
        :subText="selectedFolder ? selectedFolder.folderName : ''"
        :clickable="isClickableBreadcrumb"
        :title="breadcrumbTitle"
        @click="isClickableBreadcrumb ? handleMyPresentationButtonClick() : false"
      />
      <!-- Breadcumb just for EU user, keep showing nomatter user is searching or not -->
      <page-breadcrumb
        v-if="isEURegion"
        v-show="$route.name === 'SharedWithMe' || $route.params.folderId ||
          (!presentationsAndFoldersLoading && !empty)"
        category="workspace"
        :text="breadcrumbCategory"
        :subText="selectedFolder ? selectedFolder.folderName : ''"
        :clickable="isClickableBreadcrumb"
        :title="breadcrumbTitle"
        @click="isClickableBreadcrumb ? handleMyPresentationButtonClick() : false"
      />
      <tool-box v-show="!empty" />
    </div>

    <div
      class="flex-grow"
      :class="{'flex items-center justify-center mr-4 ml-4 md:px-6':
        empty || presentationsAndFoldersLoading
      }"
    >
      <template
        v-if="presentationsAndFoldersLoading"
      >
        <InlineLoader
          :additionalClasses="['-mt-8', {
            '-mt-16': $route.name === 'SharedWithMe'}]"
        />
      </template>

      <template v-else>
        <div class="flex items-center justify-center h-full" v-if="empty">
          <not-found v-if="searchValue" />
          <on-boarding-box
            v-if="!searchValue && !$route.params.folderId"
            :shared="$route.name === 'SharedWithMe'"
          />
          <div v-if="!searchValue && $route.params.folderId">
            <empty />
            <tool-box
              class="flex justify-center w-full mt-4"
              :isHideSortBy="true"
            />
          </div>
        </div>

        <main-table v-else class="pb-12" />
      </template>
    </div>
    <LoadingScreen v-if="isCreatingNewPresentation" />
    <UseAhaSlidesFor v-if="currentUser.id && hasPresentations" />
  </div>
</template>
<script>
import { mapState, mapGetters, mapActions } from 'vuex'

// configs
import configs from '@/config'

// event bus
import EventBus from '@/eventBus'

// utils
import utils from '@/util'
import { preloadTinyMCE } from '@/utils/script.utils'

// constants
import constants from '@/constant'

// components
import InvitationNotifications from '@/components-v2/common/global-notification/InvitationNotifications.vue'
import EduYearlyNotification from '@/components-v2/common/global-notification/EduYearlyNotification.vue'
import PublicAnnouncement from '@/components-v2/common/global-notification/PublicAnnouncement.vue'
import TemplatePanel from '@/components-v2/common/template-library/TemplatePanel.vue'
import ReportPanel from '@/components-v2/presentation-report/ReportPanel.vue'
import NotFound from './NotFound.vue'
import MainTable from './main-table/MainTable.vue'
import PageBreadcrumb from '../common/page-breadcrumb/PageBreadcrumb.vue'
import ToolBox from './ToolBox.vue'
import Empty from '@/components-v2/my-presentations/Empty.vue'
import LoadingScreen from '@/components-v2/common/loading/LoadingScreen.vue'
import SwitchTabSearch from '@/components-v2/template/SwitchTabSearch.vue'
import InlineLoader from '@/components-v2/common/loading/InlineLoader.vue'
import UseAhaSlidesFor from '@/components-v2/my-presentations/UseAhaSlideFor.vue'
import OnBoardingBox from '@/components-v2/my-presentations/OnBoardingBox.vue'

const { gaCategories } = constants
const WHITE_LABEL = 'white-label'
export default {
  components: {
    NotFound,
    MainTable,
    PageBreadcrumb,
    ToolBox,
    InvitationNotifications,
    EduYearlyNotification,
    TemplatePanel,
    ReportPanel,
    Empty,
    PublicAnnouncement,
    LoadingScreen,
    SwitchTabSearch,
    InlineLoader,
    UseAhaSlidesFor,
    OnBoardingBox,
  },
  data() {
    return {
      domain: '',
      searchValue: '',
      presentationsAndFoldersLoading: true,
      showingGlobalNotification: true,
      notifications: [],
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.currentUser,
      currentAccount: (state) => state.currentAccount,
      folders: (state) => state.folders,
      isPresenterWhiteLabel: (state) => state.isPresenterWhiteLabel,
      numberOfPresentationPages: (state) => state.numbers.numberOfPresentationPages,
      numberOfLoadedPresentationPages: (state) => state.numbers.numberOfLoadedPresentationPages,
      morePresentationsLoading: (state) => state.loaders.morePresentationsLoading,
      selectedFolderId: (state) => state.selections.selectedFolderId,
      presentations: (state) => state.presentations,
      isServerConnectable: (state) => state.indicators.isServerConnectable,
      selectedSortCriteria: (state) => state.selections.selectedSortCriteria,
      isCreatingNewPresentation: (state) => state.isCreatingNewPresentation,
    }),
    ...mapGetters(['selectedFolder']),
    ...mapGetters({
      selectedPresentationToGetReport: 'presentationReport/selectedPresentation',
    }),
    empty() {
      if (this.selectedFolderId) {
        return this.presentations.length === 0
      }

      return this.presentations.length === 0 && this.folders.length === 0
    },
    breadcrumbCategory() {
      if (this.$route.name === 'Team') {
        return this.currentAccount.name
      }
      return 'Personal'
    },
    breadcrumbTitle() {
      if (this.$route.name === 'SharedWithMe') {
        return 'Shared with Me'
      }
      if (this.$route.name === 'Team') {
        return 'Team Presentations'
      }
      return this.$store.getters.selectedFolder ? this.$store.getters.selectedFolder.folderName : 'My Presentations'
    },
    isClickableBreadcrumb() {
      if (this.selectedFolder) {
        return true
      }
      return false
    },
    isEURegion() {
      return configs.region === constants.euRegion
    },
    isSearching() {
      return this.$route.query.q
    },
    hasPresentations() {
      return this.presentations && this.presentations.length > 0
    },
  },

  watch: {
    'currentUser.whiteLabelLink': function handleWhiteLableLinkChange() {
      if (this.currentUser.plan === WHITE_LABEL) {
        this.domain = this.currentUser.whiteLabelLink
      }
    },
    selectedSortCriteria() {
      this.setNumberOfLoadedPresentationPagesAction(1)
      this._getPresentations()
    },
    $route: function handleChangeRoute(newRoute) {
      if (newRoute.name === 'Presentations') {
        this.searchValue = this.$route.query.q || ''
      } else {
        this.searchValue = ''
      }

      if (newRoute.name === 'FolderDetail') {
        this.setSelectedFolderIdAction(parseInt(newRoute.params.folderId, 10))
      } else {
        this.setSelectedFolderIdAction(null)
      }

      if (newRoute.name === 'SharedWithMe') {
        this.setFoldersAction([])
        this.setPresentationsAction([])
      }

      this.setNumberOfLoadedPresentationPagesAction(1)
      this._getPresentations()
    },
  },
  async created() {
    setTimeout(() => {
      // Case: No Team but in Team route..
      if (this.$store.state.currentUser &&
        !this.$store.state.currentUser.accountId
        && (this.$route.name === 'Team' || this.$route.name === 'TeamMember')) {
        this.$router.push('/apps/presentations')
      }
    }, 1000)
  },
  async mounted() {
    // scroll to top to see global notifications. Avoid heading overlap
    window.scrollTo({ top: 0, behavior: 'smooth' })

    this.domain = configs.domain

    this.searchValue = this.$route.query.q || ''

    this.setNumberOfLoadedPresentationPagesAction(1)
    window.addEventListener('scroll', this.handleMyPresentationsScroll)
    EventBus.$on('reload-presentations-and-folders', this._getPresentations)

    if (this.$route.params.folderId) {
      if (this.isFolderIdValid(this.$route.params.folderId)) {
        this.setSelectedFolderIdAction(parseInt(this.$route.params.folderId, 10))
      } else {
        this.$router.push({
          name: 'Presentations',
        })
      }
    }
    await this._getPresentations()
    preloadTinyMCE()
  },
  destroyed() {
    this.$store.state.openImportModal = false
    window.onscroll = null
    this.setSelectedFolderIdAction(null)
    EventBus.$off('reload-presentations-and-folders')
    window.removeEventListener('scroll', this.handleMyPresentationsScroll)
    this.unsubscribeReportAction()
    this.clearPresentationReportAction()
  },
  methods: {
    ...mapActions([
      'getPresentationsAction',
      'getSharedPresentationsAction',
      'setSelectedFolderIdAction',
      'getFoldersAction',
      'setFoldersAction',
      'setPresentationsAction',
      'setIsServerConnectableAction',
      'setNumberOfLoadedPresentationPagesAction',
      'setMorePresentationsLoadingAction',
      'setCurrentAccountAction',
    ]),
    ...mapActions('presentationReport', ['clearPresentationReportAction', 'unsubscribeReportAction']),
    isFolderIdValid(folderId) {
      return !Number.isNaN(parseInt(folderId, 10))
    },
    async _getPresentations() {
      if (this.$route.name === 'Presentations' || this.$route.name === 'FolderDetail') {
        await this.getPresentationsAndFolders()
      } else if (this.$route.name === 'SharedWithMe') {
        await this.getSharedPresentations()
      } else if (this.$route.name === 'Team') {
        if (configs.region === constants.euRegion) {
          this.$router.push({ name: 'Presentations' })
        } else {
          await this.getTeamPresentations()
        }
      }
      this.syncWorkingMode()
    },
    syncWorkingMode() {
      // prevent user auto input link in url bar
      if (this.$route.name === 'Presentations' && this.$store.state.workingMode === constants.workspace.TEAM) {
        this.$store.state.workingMode = constants.workspace.PERSONAL
        window.localStorage.setItem(constants.workspace.WORKING_MODE, constants.workspace.PERSONAL)
      } else if (this.$route.name === 'Team' && this.$store.state.workingMode === constants.workspace.PERSONAL) {
        this.$store.state.workingMode = constants.workspace.TEAM
        window.localStorage.setItem(constants.workspace.WORKING_MODE, constants.workspace.TEAM)
      }
    },
    async getSharedPresentations() {
      if (!this.isServerConnectable) return

      const params = {
        page: this.numberOfLoadedPresentationPages,
        sortOrder: this.selectedSortCriteria.sortOrder,
        sortColumn: this.selectedSortCriteria.sortColumn,
      }

      if (this.searchValue.trim()) {
        params.textSearch = this.searchValue.trim()
      }

      // show loader
      if (this.numberOfLoadedPresentationPages === 1) {
        this.presentationsAndFoldersLoading = true
      } else {
        this.setMorePresentationsLoadingAction(true)
      }

      try {
        await this.getSharedPresentationsAction(params)
      } catch (error) {
        this.setIsServerConnectableAction(false)
        utils.gaTrackEvent(gaCategories.PRESENTATION_LIST, 'Shared Presentations > Load > Failed')
        throw error
      } finally {
        this.presentationsAndFoldersLoading = false
        this.setMorePresentationsLoadingAction(false)
      }

      utils.gaTrackEvent(gaCategories.PRESENTATION_LIST, 'Shared Presentations > Load > Succeeded')

      if (this.currentUser.plan === WHITE_LABEL) {
        this.domain = this.currentUser.whiteLabelLink
      }
    },

    async getPresentationsAndFolders() {
      if (!this.isServerConnectable) return
      const params = {
        page: this.numberOfLoadedPresentationPages,
        sortOrder: this.selectedSortCriteria.sortOrder,
        sortColumn: this.selectedSortCriteria.sortColumn,
        includeShared: true,
      }

      if (this.searchValue.trim()) {
        params.textSearch = this.searchValue.trim()
      } else {
        params.folderId = this.selectedFolderId || null
      }

      // show loader
      if (this.numberOfLoadedPresentationPages === 1) {
        this.presentationsAndFoldersLoading = true
      } else {
        this.setMorePresentationsLoadingAction(true)
      }

      try {
        if (this.numberOfLoadedPresentationPages === 1) {
          this.setPresentationsAction([])
          await this.getFoldersAction(params)
        }

        await this.getPresentationsAction(params)
      } catch (error) {
        this.setIsServerConnectableAction(false)
        utils.gaTrackEvent(gaCategories.PRESENTATION_LIST, 'Presentations > Load > Failed')
        throw error
      } finally {
        this.presentationsAndFoldersLoading = false
        this.setMorePresentationsLoadingAction(false)
      }

      // Disable for less noise to event
      // utils.gaTrackEvent(gaCategories.PRESENTATION_LIST, 'Presentations > Load > Succeeded')

      if (this.currentUser.plan === WHITE_LABEL) {
        this.domain = this.currentUser.whiteLabelLink
      }
    },
    async getTeamPresentations() {
      if (!this.isServerConnectable) return
      const params = {
        page: this.numberOfLoadedPresentationPages,
        sortOrder: this.selectedSortCriteria.sortOrder,
        sortColumn: this.selectedSortCriteria.sortColumn,
        fromWorkspace: true,
      }
      this.setFoldersAction([])
      // show loader
      if (this.numberOfLoadedPresentationPages === 1) {
        this.presentationsAndFoldersLoading = true
      } else {
        this.setMorePresentationsLoadingAction(true)
      }

      try {
        if (this.numberOfLoadedPresentationPages === 1) {
          this.setPresentationsAction([])
        }

        await this.getPresentationsAction(params)
      } catch (error) {
        if (error.status === 400) {
          // remove out of team but member state remaining call api, force reload personal mode
          this.$store.state.workingMode = constants.workspace.PERSONAL
          this.setCurrentAccountAction({})
          this.$router.push({ name: 'Presentations' })
        } else {
          this.setIsServerConnectableAction(false)
          utils.gaTrackEvent(gaCategories.PRESENTATION_LIST, 'Presentations > Load > Failed')
          throw error
        }
      } finally {
        this.presentationsAndFoldersLoading = false
        this.setMorePresentationsLoadingAction(false)
      }
      if (this.currentUser.plan === WHITE_LABEL) {
        this.domain = this.currentUser.whiteLabelLink
      }
    },
    async handleMyPresentationsScroll() {
      if (this.presentationsAndFoldersLoading) return

      if (this.morePresentationsLoading) return

      if (this.numberOfLoadedPresentationPages >= this.numberOfPresentationPages) return

      if (!this.$refs.myPresentations) return

      if (window.innerHeight + window.scrollY >= this.$refs.myPresentations.offsetHeight - 100) {
        this.setNumberOfLoadedPresentationPagesAction(this.numberOfLoadedPresentationPages + 1)
        await this._getPresentations()
      }
    },
    handleMyPresentationButtonClick() {
      this.$router.push({ name: 'Presentations' })
    },
  },
}
</script>
